import React, { useState, useRef, useEffect } from 'react';
import {
    User,
    Mail,
    Phone,
    Lock,
    Camera,
    Check,
    AlertCircle,
    X,
    Eye,
    EyeOff,
    RefreshCw,
    Copy,
    Wallet,
    MessageCircle,
    Save,
    Edit,
    ChevronDown,
    Settings,
    Share2,
    Shield,
    CreditCard,
    Users,
    Sparkles
} from 'lucide-react';
import { profileService } from '../services/profileService';
import { authService } from '../services/authService';
import TelegramIntegration from '../pages/TelegramIntegration';

// ✅ НОВЫЙ ИМПОРТ: useAuth
import { useAuth } from '../contexts/AuthContext';

const ProfilePageMobile = () => {
    // ✅ ИСПОЛЬЗУЕМ useAuth ВМЕСТО ЛОКАЛЬНОГО СОСТОЯНИЯ
    const { user: authUser, loading: authLoading, refreshUser } = useAuth();

    const [activeSection, setActiveSection] = useState('general');

    // ✅ ИНИЦИАЛИЗИРУЕМ profile ИЗ authUser
    const [profile, setProfile] = useState({
        id: null,  // ✅ ДОБАВЬТЕ ЭТУ СТРОКУ
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        login: '',
        level: 'Новичок',
        avatar: null,
        referral_link: '',
        telegram_login: '',
        vk_link: '',
        payeer_wallet: '',
        fk_wallet: '',
        telegram_connected: false
    });

    const [formData, setFormData] = useState({
        id: null,  // ✅ ДОБАВЬТЕ ЭТУ СТРОКУ
        first_name: '',
        last_name: '',
        phone: '',
        telegram_login: '',
        vk_link: '',
        payeer_wallet: '',
        fk_wallet: '',
        current_password: '',
        new_password: '',
        confirm_password: ''
    });

    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [copied, setCopied] = useState(false);

    const fileInputRef = useRef(null);

    // ✅ ИСПРАВЛЕНО: Инициализация данных из authUser
    useEffect(() => {
        if (authUser) {
            console.log('✅ Инициализация профиля из AuthContext:', authUser);

            setProfile({
                id: authUser.id,  // ✅ ДОБАВЬТЕ ЭТУ СТРОКУ
                first_name: authUser.first_name || '',
                last_name: authUser.last_name || '',
                email: authUser.email || '',
                phone: authUser.phone || '',
                login: authUser.login || '',
                level: authUser.level || 'Новичок',
                avatar: authUser.avatar || null,
                referral_link: authUser.referral_link || '',
                telegram_login: authUser.telegram_login || '',
                vk_link: authUser.vk_link || '',
                payeer_wallet: authUser.payeer_wallet || '',
                fk_wallet: authUser.fk_wallet || '',
                telegram_connected: authUser.telegram_connected || false
            });

            setFormData({
                id: authUser.id,  // ✅ ДОБАВЬТЕ ЭТУ СТРОКУ
                first_name: authUser.first_name || '',
                last_name: authUser.last_name || '',
                phone: authUser.phone || '',
                telegram_login: authUser.telegram_login || '',
                vk_link: authUser.vk_link || '',
                payeer_wallet: authUser.payeer_wallet || '',
                fk_wallet: authUser.fk_wallet || '',
                current_password: '',
                new_password: '',
                confirm_password: ''
            });
        }
    }, [authUser]);

    // ✅ УДАЛЕНО: весь код loadProfile() и useEffect с запросом
    // Теперь данные берутся из AuthContext

    // Обработчик изменения полей формы
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (error) setError(null);
    };

    // ✅ ИСПРАВЛЕНО: Сохранение основных данных с обновлением контекста
    const handleSaveGeneral = async () => {
        try {
            setError(null);
            setIsSaving(true);

            const response = await profileService.updateProfile({
                first_name: formData.first_name,
                last_name: formData.last_name,
                phone: formData.phone,
                telegram_login: formData.telegram_login,
                vk_link: formData.vk_link
            });

            if (response.success) {
                // ✅ ОБНОВЛЯЕМ КОНТЕКСТ
                await refreshUser();

                setSuccess('Данные успешно обновлены');
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError(response.message || 'Ошибка обновления профиля');
            }
        } catch (error) {
            console.error('Profile update error:', error);
            setError('Произошла ошибка при обновлении профиля');
        } finally {
            setIsSaving(false);
        }
    };

    // Остальные обработчики остаются без изменений...

    // Сохранение кошельков
    // ✅ ИСПРАВЛЕНО: Сохранение кошельков
    const handleSaveWallet = async () => {
        try {
            setError(null);
            setIsSaving(true);

            const response = await profileService.updateProfile({
                payeer_wallet: formData.payeer_wallet,
                fk_wallet: formData.fk_wallet
            });

            if (response.success) {
                // ✅ ОБНОВЛЯЕМ КОНТЕКСТ
                await refreshUser();

                setSuccess('Данные кошельков обновлены');
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError(response.message || 'Ошибка обновления кошельков');
            }
        } catch (error) {
            console.error('Wallets update error:', error);
            setError('Произошла ошибка при обновлении кошельков');
        } finally {
            setIsSaving(false);
        }
    };

    // Смена пароля
    const handlePasswordChange = async () => {
        if (!formData.current_password || !formData.new_password || !formData.confirm_password) {
            setError('Заполните все поля для смены пароля');
            return;
        }

        if (formData.new_password !== formData.confirm_password) {
            setError('Новые пароли не совпадают');
            return;
        }

        if (formData.new_password.length < 6) {
            setError('Новый пароль должен содержать минимум 6 символов');
            return;
        }

        try {
            setError(null);
            setIsSaving(true);

            const response = await authService.changePassword({
                current_password: formData.current_password,
                new_password: formData.new_password,
                new_password_confirmation: formData.confirm_password
            });

            if (response.success) {
                setFormData(prev => ({
                    ...prev,
                    current_password: '',
                    new_password: '',
                    confirm_password: ''
                }));
                setSuccess('Пароль успешно изменен');
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError(response.message || 'Ошибка смены пароля');
            }

        } catch (error) {
            console.error('Password change error:', error);
            setError('Произошла ошибка при смене пароля');
        } finally {
            setIsSaving(false);
        }
    };

    // Обработка загрузки аватара
    // ✅ ИСПРАВЛЕНО: Обработчик загрузки аватара
    const handleAvatarChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            setError('Размер файла не должен превышать 2MB');
            return;
        }

        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            setError('Разрешены только изображения (JPEG, PNG, JPG, GIF)');
            return;
        }

        try {
            setError(null);
            setIsSaving(true);

            const reader = new FileReader();
            reader.onload = (e) => setAvatarPreview(e.target.result);
            reader.readAsDataURL(file);

            const response = await profileService.updateAvatar(file);

            if (response.success) {
                // ✅ ОБНОВЛЯЕМ КОНТЕКСТ
                await refreshUser();

                setProfile(prev => ({
                    ...prev,
                    avatar: response.data.avatar_url
                }));
                setSuccess('Аватар успешно обновлен');
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError(response.message || 'Ошибка загрузки аватара');
                setAvatarPreview(null);
            }

        } catch (error) {
            console.error('Avatar upload error:', error);
            setError('Произошла ошибка при загрузке аватара');
            setAvatarPreview(null);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCopyReferralLink = () => {
        navigator.clipboard.writeText(profile.referral_link);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const togglePasswordVisibility = (field) => {
        setShowPassword(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    // Функция для получения инициалов
    const getInitials = (firstName, lastName) => {
        const first = firstName ? firstName.charAt(0).toUpperCase() : '';
        const last = lastName ? lastName.charAt(0).toUpperCase() : '';
        return first + last || 'U';
    };

    // Компонент для навигации по секциям
    const SectionTabs = () => {
        const sections = [
            { id: 'general', label: 'Основные', icon: User },
            { id: 'security', label: 'Безопасность', icon: Shield },
            { id: 'wallet', label: 'Кошельки', icon: CreditCard },
            { id: 'referral', label: 'Рефералы', icon: Users },
            { id: 'telegram', label: 'Telegram', icon: MessageCircle }
        ];

        return (
            <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
                <div className="flex overflow-x-auto scrollbar-hide px-4">
                    {sections.map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            onClick={() => setActiveSection(id)}
                            className={`flex items-center space-x-2 py-4 px-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap min-w-0 ${
                                activeSection === id
                                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <Icon className="w-4 h-4 flex-shrink-0" />
                            <span className="hidden sm:inline">{label}</span>
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    // Компонент уведомлений
    const Notification = ({ type, message, onClose }) => {
        if (!message) return null;

        const bgColor = type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
        const textColor = type === 'success' ? 'text-green-700' : 'text-red-700';
        const iconColor = type === 'success' ? 'text-green-500' : 'text-red-500';
        const Icon = type === 'success' ? Check : AlertCircle;

        return (
            <div className={`rounded-xl border p-4 mb-4 ${bgColor}`}>
                <div className="flex items-start">
                    <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
                    <div className="ml-3 flex-1 min-w-0">
                        <p className={`text-sm ${textColor} break-words`}>{message}</p>
                    </div>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className={`ml-4 ${textColor} hover:opacity-75 flex-shrink-0`}
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
        );
    };

    // Заголовок профиля
    const ProfileHeader = () => (
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-6">
            <div className="flex items-center space-x-4">
                <div className="relative flex-shrink-0">
                    {avatarPreview || profile.avatar ? (
                        <img
                            src={avatarPreview || profile.avatar}
                            alt="Аватар"
                            className="w-16 h-16 rounded-full object-cover border-2 border-white"
                        />
                    ) : (
                        <div className="w-16 h-16 rounded-full bg-white/20 border-2 border-white flex items-center justify-center">
                            <span className="text-white text-xl font-semibold">
                                {getInitials(profile.first_name, profile.last_name)}
                            </span>
                        </div>
                    )}
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute -bottom-1 -right-1 bg-white text-blue-600 rounded-full p-1.5 shadow-sm hover:shadow-md transition-shadow"
                        disabled={isSaving}
                    >
                        <Camera className="w-3 h-3" />
                    </button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleAvatarChange}
                        accept="image/*"
                        className="hidden"
                    />
                </div>

                <div className="flex-1 min-w-0">
                    <h1 className="text-xl font-bold text-white truncate">
                        {profile.first_name && profile.last_name
                            ? `${profile.first_name} ${profile.last_name}`
                            : profile.login || 'Пользователь'
                        }
                    </h1>
                    <p className="text-blue-100 truncate">{profile.login}</p>
                </div>
            </div>
        </div>
    );

    // Компонент загрузки
     if (authLoading) {
         return (
             <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                 <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
                     <div className="flex items-center space-x-3">
                         <RefreshCw className="w-5 h-5 animate-spin text-blue-600" />
                         <span className="text-gray-600">Загрузка профиля...</span>
                     </div>
                 </div>
             </div>
         );
     }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Заголовок профиля */}
            <ProfileHeader />

            {/* Навигация по секциям */}
            <SectionTabs />

            {/* Контент */}
            <div className="pb-20">
                {/* Уведомления */}
                <Notification
                    type={error ? 'error' : 'success'}
                    message={error || success}
                    onClose={() => {
                        setError(null);
                        setSuccess('');
                    }}
                />

                {/* Секция: Основные данные */}
                {activeSection === 'general' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-xl shadow-sm">
                            <h2 className="text-lg font-bold text-gray-900 mb-4">Основные данные</h2>

                            <div className="space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Имя</label>
                                        <input
                                            type="text"
                                            name="first_name"
                                            value={formData.first_name}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                                            placeholder="Введите ваше имя"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Фамилия</label>
                                        <input
                                            type="text"
                                            name="last_name"
                                            value={formData.last_name}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                                            placeholder="Введите вашу фамилию"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                                    <input
                                        type="email"
                                        value={profile.email}
                                        disabled
                                        className="w-full px-3 py-3 border border-gray-200 rounded-xl bg-gray-100 text-gray-500 text-sm"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Email нельзя изменить</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Телефон</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                                        placeholder="+7 (___) ___-__-__"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Telegram</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">@</span>
                                        <input
                                            type="text"
                                            name="telegram_login"
                                            value={formData.telegram_login}
                                            onChange={handleInputChange}
                                            className="w-full pl-8 pr-3 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                                            placeholder="username"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">VK</label>
                                    <input
                                        type="text"
                                        name="vk_link"
                                        value={formData.vk_link}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                                        placeholder="Ссылка на VK профиль"
                                    />
                                </div>
                            </div>

                            <button
                                onClick={handleSaveGeneral}
                                disabled={isSaving}
                                className="w-full mt-6 bg-blue-600 text-white px-4 py-3 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                            >
                                {isSaving ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                <span>{isSaving ? 'Сохранение...' : 'Сохранить изменения'}</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Секция: Безопасность */}
                {activeSection === 'security' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-xl shadow-sm p-6">
                            <h2 className="text-lg font-bold text-gray-900 mb-4">Безопасность</h2>

                            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                                <p className="text-yellow-800 text-sm">
                                    <strong>Важно:</strong> После изменения пароля вам потребуется войти в систему заново.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Текущий пароль</label>
                                    <div className="relative">
                                        <input
                                            type={showPassword.current ? 'text' : 'password'}
                                            name="current_password"
                                            value={formData.current_password}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-3 pr-10 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                                            placeholder="Введите текущий пароль"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => togglePasswordVisibility('current')}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                        >
                                            {showPassword.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Новый пароль</label>
                                    <div className="relative">
                                        <input
                                            type={showPassword.new ? 'text' : 'password'}
                                            name="new_password"
                                            value={formData.new_password}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-3 pr-10 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                                            placeholder="Введите новый пароль"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => togglePasswordVisibility('new')}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                        >
                                            {showPassword.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Подтверждение пароля</label>
                                    <div className="relative">
                                        <input
                                            type={showPassword.confirm ? 'text' : 'password'}
                                            name="confirm_password"
                                            value={formData.confirm_password}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-3 pr-10 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                                            placeholder="Подтвердите новый пароль"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => togglePasswordVisibility('confirm')}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                        >
                                            {showPassword.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handlePasswordChange}
                                disabled={isSaving}
                                className="w-full mt-6 bg-red-600 text-white px-4 py-3 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                            >
                                {isSaving ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Lock className="w-4 h-4" />
                                )}
                                <span>{isSaving ? 'Изменение...' : 'Изменить пароль'}</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Секция: Кошельки */}
                {activeSection === 'wallet' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-xl shadow-sm p-6">
                            <h2 className="text-lg font-bold text-gray-900 mb-4">Кошельки</h2>

                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                                <p className="text-blue-800 text-sm">
                                    <strong>Внимание:</strong> Укажите корректные адреса кошельков для получения выплат.
                                    Проверьте адреса перед сохранением.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">USDT BEP-20</label>
                                    <input
                                        type="text"
                                        name="payeer_wallet"
                                        value={formData.payeer_wallet}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                                        placeholder="0x......."
                                    />
                                </div>

                                {/*<div>*/}
                                {/*    <label className="block text-sm font-medium text-gray-700 mb-2">FKWallet кошелек</label>*/}
                                {/*    <input*/}
                                {/*        type="text"*/}
                                {/*        name="fk_wallet"*/}
                                {/*        value={formData.fk_wallet}*/}
                                {/*        onChange={handleInputChange}*/}
                                {/*        className="w-full px-3 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"*/}
                                {/*        placeholder="FK1234567890"*/}
                                {/*    />*/}
                                {/*</div>*/}
                            </div>

                            <button
                                onClick={handleSaveWallet}
                                disabled={isSaving}
                                className="w-full mt-6 bg-green-600 text-white px-4 py-3 rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                            >
                                {isSaving ? (
                                    <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Wallet className="w-4 h-4" />
                                )}
                                <span>{isSaving ? 'Сохранение...' : 'Сохранить кошельки'}</span>
                            </button>
                        </div>
                    </div>
                )}

                {/* Секция: Рефералы */}
                {activeSection === 'referral' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-xl shadow-sm p-6">
                            <h2 className="text-lg font-bold text-gray-900 mb-4">Реферальная программа</h2>

                            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4 mb-6">
                                <div className="flex items-center space-x-3 mb-4">
                                    <Sparkles className="w-6 h-6 text-purple-600 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <h3 className="font-semibold text-purple-900">Ваша реферальная ссылка</h3>
                                        <p className="text-purple-700 text-sm">Поделитесь с друзьями и зарабатывайте</p>
                                    </div>
                                </div>

                                {/* Мобильная версия - вертикальная */}
                                <div className="flex flex-col sm:hidden space-y-2">
                                    <input
                                        type="text"
                                        value={profile.referral_link}
                                        readOnly
                                        className="w-full px-3 py-2 bg-white border border-purple-300 rounded-lg text-sm"
                                    />
                                    <button
                                        onClick={handleCopyReferralLink}
                                        className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
                                    >
                                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                        <span className="text-sm">{copied ? 'Скопировано!' : 'Копировать'}</span>
                                    </button>
                                </div>

                                {/* Десктопная версия - горизонтальная */}
                                <div className="hidden sm:flex items-center space-x-2">
                                    <input
                                        type="text"
                                        value={profile.referral_link}
                                        readOnly
                                        className="flex-1 px-3 py-2 bg-white border border-purple-300 rounded-lg text-sm min-w-0"
                                    />
                                    <button
                                        onClick={handleCopyReferralLink}
                                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-1 flex-shrink-0"
                                    >
                                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                        <span className="text-sm whitespace-nowrap">{copied ? 'Скопировано!' : 'Копировать'}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Секция: Telegram интеграция */}
                {activeSection === 'telegram' && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                                <MessageCircle className="w-5 h-5 mr-2 text-blue-600 flex-shrink-0" />
                                <span className="truncate">Telegram уведомления</span>
                            </h2>

                            {/* Обертка с контролем переполнения */}
                            <div className="w-full overflow-hidden">
                                <TelegramIntegration user={profile} isProfilePage={true} />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        </div>
    );
};

export default ProfilePageMobile;
